import type { EfteruddannelsesperiodeIndberetningOverviewModel } from "@/interfaces/efteruddannelsesperiodeIndberetningOverview";
import CleTable, { type CleTableColumn } from "@cle/components/CleTable";
import { useNavigate } from "react-router";
import { formatLessons } from "@cle/utils/lessonLimits";
import { getReportingPeriodStatusPresentation } from "./reportingPeriodStatus";

type ReportingPeriodsTableProps = {
  isError?: boolean;
  isLoading?: boolean;
  reportPeriods: EfteruddannelsesperiodeIndberetningOverviewModel[];
};

type ReportingPeriodTableRow =
  EfteruddannelsesperiodeIndberetningOverviewModel & {
    id: string;
  };

const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) {
    return "-";
  }

  return new Intl.DateTimeFormat("da-DK", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(date));
};

const ReportingPeriodsTable = ({
  isError = false,
  isLoading = false,
  reportPeriods,
}: ReportingPeriodsTableProps) => {
  const navigate = useNavigate();
  const tableRows: ReportingPeriodTableRow[] = reportPeriods.map((period) => ({
    ...period,
    id: period.efteruddannelsesperiodeId,
  }));

  const columns: CleTableColumn<ReportingPeriodTableRow>[] = [
    {
      id: "period",
      header: "Periode",
      cell: (period) =>
        `${formatDate(period.periodeFraDato)} - ${formatDate(period.periodeTilDatoMinusEnDag)}`,
    },
    {
      id: "deadline",
      header: "Frist",
      cell: (period) => formatDate(period.frist),
    },
    {
      id: "status",
      header: "Status",
      cell: (period) => {
        const status = getReportingPeriodStatusPresentation(period);

        return (
          <span className={`cle-status-badge ${status.className}`}>
            {status.label}
          </span>
        );
      },
    },
    {
      id: "reportedLessons",
      header: "Indberettede lektioner",
      cell: (period) => formatLessons(period.lektionerIndberettet?.sum ?? 0),
    },

    {
      id: "transferredToNext",
      header: "Overført til næste periode",
      cell: (period) =>
        formatLessons(period.lektionerOverfoeresTilNaeste?.sum ?? 0),
    },
    {
      id: "reportedDate",
      header: "Indberetningsdato",
      cell: (period) => formatDate(period.indsendt),
    },
  ];

  return (
    <CleTable
      columns={columns}
      data={tableRows}
      emptyDescription="Når efteruddannelsesperioder er tilgængelige, vises de her."
      emptyTitle="Ingen indberetninger fundet"
      errorMessage="Indberetninger kunne ikke indlæses lige nu."
      isError={isError}
      isLoading={isLoading}
      loadingRowCount={3}
      rowAction={{
        onClick: (periodId) => {
          void navigate(periodId);
        },
      }}
    />
  );
};

export default ReportingPeriodsTable;
